import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Users, Award, Target, Heart, TrendingUp, CheckCircle, Star, Briefcase, Scale } from 'lucide-react';
import { images } from '../mock';
import InnerPageHero from '../components/InnerPageHero';
import InnerPageBanner from '../components/InnerPageBanner';

const AboutPage = () => {
  return (
    <>
      <Helmet>
        <title>About Us | Trusted Trademark Filer - Expert IP Protection Services</title>
        <meta name="description" content="Learn about Trusted Trademark Filer, your trusted partner in intellectual property protection. Professional trademark registration and legal services with a proven track record." />
      </Helmet>
      <div className="min-h-screen">
      {/* Hero Section */}
      <InnerPageHero
        badge="ABOUT US"
        title="About Trusted Trademark Filer"
        description="Your trusted partner in intellectual property protection, dedicated to securing your brand's future with professional trademark and legal services."
      />

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src={images.about}
                alt="Professional Team"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                Our Mission
              </h2>
              <p className="text-base text-gray-700 leading-relaxed">
                At Trusted Trademark Filer, we believe that every business deserves comprehensive intellectual property protection. Our mission is to make trademark registration accessible, transparent, and efficient for businesses of all sizes.
              </p>
              <p className="text-base text-gray-700 leading-relaxed">
                We combine legal expertise with modern technology to provide seamless trademark registration services. From initial consultation to ongoing monitoring, we're with you every step of the way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-b from-white to-[#E2F8FE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-base text-gray-600">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300">
              <div className="bg-[#E2F8FE] w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Shield className="h-8 w-8 text-[#FF4A2A]" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Integrity
              </h3>
              <p className="text-gray-600">
                We maintain the highest ethical standards in all our interactions, providing honest guidance and transparent pricing.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300">
              <div className="bg-[#E2F8FE] w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Award className="h-8 w-8 text-[#FF4A2A]" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Excellence
              </h3>
              <p className="text-gray-600">
                We strive for excellence in every trademark filing, leveraging our expertise to achieve the highest approval rates.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300">
              <div className="bg-[#E2F8FE] w-16 h-16 rounded-full flex items-center justify-center mb-6">
                <Heart className="h-8 w-8 text-[#FF4A2A]" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Client-Focused
              </h3>
              <p className="text-gray-600">
                Your success is our priority. We provide personalized service and dedicated support throughout your trademark journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                Our Approach
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-[#E2F8FE] p-3 rounded-lg flex-shrink-0">
                    <Target className="h-6 w-6 text-[#FF4A2A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Comprehensive Research
                    </h3>
                    <p className="text-gray-600">
                      We conduct thorough trademark searches to ensure your mark is available and registrable before filing.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#E2F8FE] p-3 rounded-lg flex-shrink-0">
                    <Users className="h-6 w-6 text-[#FF4A2A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Expert Guidance
                    </h3>
                    <p className="text-gray-600">
                      Our experienced team provides personalized guidance, helping you make informed decisions about your intellectual property.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[#E2F8FE] p-3 rounded-lg flex-shrink-0">
                    <TrendingUp className="h-6 w-6 text-[#FF4A2A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Ongoing Support
                    </h3>
                    <p className="text-gray-600">
                      From filing to registration and beyond, we provide continuous monitoring and protection for your trademarks.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src={images.team}
                alt="Team Collaboration"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Creative Banner */}
      <InnerPageBanner onOpenPopup={() => {}} />

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-[#FF4A2A] to-[#E63E1F] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold mb-2">500+</div>
              <div className="text-xl opacity-90">Trademarks Filed</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">95%</div>
              <div className="text-xl opacity-90">Approval Rate</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">300+</div>
              <div className="text-xl opacity-90">Happy Clients</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">24/7</div>
              <div className="text-xl opacity-90">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Businesses Choose Us
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              We're more than just a trademark service - we're your long-term intellectual property partner.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-[#E2F8FE] to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-white w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-md">
                <CheckCircle className="h-7 w-7 text-[#FF4A2A]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Transparent Pricing
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                No hidden fees or surprise charges. You'll know exactly what you're paying for upfront.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#E2F8FE] to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-white w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-md">
                <TrendingUp className="h-7 w-7 text-[#FF4A2A]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Fast Turnaround
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                We file applications quickly and efficiently, keeping your trademark process moving forward.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#E2F8FE] to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-white w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-md">
                <Users className="h-7 w-7 text-[#FF4A2A]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Dedicated Support
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Your assigned case manager is always available to answer questions and provide updates.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#E2F8FE] to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-white w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-md">
                <Briefcase className="h-7 w-7 text-[#FF4A2A]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Comprehensive Services
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                From trademarks to patents and copyrights, we handle all your intellectual property needs.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#E2F8FE] to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-white w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-md">
                <Star className="h-7 w-7 text-[#FF4A2A]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Proven Track Record
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                Our high approval rate speaks to our expertise and attention to detail in every filing.
              </p>
            </div>

            <div className="bg-gradient-to-br from-[#E2F8FE] to-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="bg-white w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-md">
                <Shield className="h-7 w-7 text-[#FF4A2A]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Ongoing Protection
              </h3>
              <p className="text-base text-gray-600 leading-relaxed">
                We don't just file - we monitor and protect your trademarks long after registration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-20 bg-gradient-to-b from-white to-[#E2F8FE]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                Our Commitment to Excellence
              </h2>
              <p className="text-base text-gray-700 leading-relaxed">
                We are committed to providing the highest quality intellectual property services. Our team of experienced professionals stays up-to-date with the latest trademark laws and USPTO regulations to ensure your application has the best chance of approval.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="bg-[#FF4A2A] p-2 rounded-lg flex-shrink-0">
                    <Scale className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Legal Expertise</h3>
                    <p className="text-base text-gray-600">Our team includes experienced trademark attorneys and legal professionals.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-[#FF4A2A] p-2 rounded-lg flex-shrink-0">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Quality Assurance</h3>
                    <p className="text-base text-gray-600">Every application undergoes thorough review before submission.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-[#FF4A2A] p-2 rounded-lg flex-shrink-0">
                    <Heart className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Client Satisfaction</h3>
                    <p className="text-base text-gray-600">Your success is our priority, and we go above and beyond to ensure satisfaction.</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src={images.workflow}
                alt="Our Commitment"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Industry Experience */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Industry Experience
            </h2>
            <p className="text-base text-gray-600 max-w-3xl mx-auto">
              We've worked with businesses across diverse industries, providing tailored intellectual property solutions
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center p-6 bg-[#E2F8FE] rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Technology</h3>
              <p className="text-sm text-gray-600">Software, hardware, and tech startups</p>
            </div>
            <div className="text-center p-6 bg-[#E2F8FE] rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Retail & E-commerce</h3>
              <p className="text-sm text-gray-600">Online stores and retail brands</p>
            </div>
            <div className="text-center p-6 bg-[#E2F8FE] rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Food & Beverage</h3>
              <p className="text-sm text-gray-600">Restaurants, food brands, beverages</p>
            </div>
            <div className="text-center p-6 bg-[#E2F8FE] rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Professional Services</h3>
              <p className="text-sm text-gray-600">Consulting, agencies, service providers</p>
            </div>
            <div className="text-center p-6 bg-[#E2F8FE] rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Healthcare</h3>
              <p className="text-sm text-gray-600">Medical practices, health tech, wellness</p>
            </div>
            <div className="text-center p-6 bg-[#E2F8FE] rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Fashion & Apparel</h3>
              <p className="text-sm text-gray-600">Clothing brands, accessories, jewelry</p>
            </div>
            <div className="text-center p-6 bg-[#E2F8FE] rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Creative Arts</h3>
              <p className="text-sm text-gray-600">Artists, designers, content creators</p>
            </div>
            <div className="text-center p-6 bg-[#E2F8FE] rounded-xl">
              <h3 className="font-bold text-gray-900 mb-2">Real Estate</h3>
              <p className="text-sm text-gray-600">Property management, real estate firms</p>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default AboutPage;